import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { subscriptionUserRepository } from "./repositories/subscription-user.repository";
import { SubscriptionUser, SubscriptionUsersFilter } from "./types/subscription-user.entity";
import { callFunctionSafe } from "src/firebase/functions";

export type { SubscriptionUsersFilter };

export type StoreSubscriptionVerification = {
  userId: string;
  subscriptionId: string;
  provider: "APPLE" | "GOOGLE" | "TAALY";
  localStatus: "ACTIVE" | "CANCELLED" | "EXPIRED";
  localEndDate: string | null;
  store: {
    status: string | string[] | null;
    endDate: string | null;
    autoRenewEnabled: boolean | null;
  };
};

export type StorePaymentAuditStatus =
  | "RECORDED"
  | "NOT_RECORDED"
  | "LEGACY_UNVERIFIABLE";

export type StorePaymentAudit = {
  storePaymentId: string;
  productId: string | null;
  purchasedAt: string | null;
  expiresAt: string | null;
  amount: number | null;
  currency: string | null;
  status: StorePaymentAuditStatus;
  firestorePaymentId: string | null;
};

export type StoreSubscriptionPaymentAudit = {
  userId: string;
  subscriptionId: string;
  provider: "APPLE" | "GOOGLE";
  localPaymentCount: number;
  storePayments: StorePaymentAudit[];
};

const subscriptionUsersService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptionUsers: builder.query<
      (SubscriptionUser & { id: string })[],
      { where?: SubscriptionUsersFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        console.log("🚀 Service: Getting subscription users with filters:", where);
        const result = await executeQuery(() => subscriptionUserRepository.findSubscriptionUsers({ where, offset, limit }));
        console.log("📦 Service: Query result:", result);
        
        if ("error" in result) {
          console.error("❌ Service: Query error:", result.error);
          return { error: result.error } as any;
        }
        
        console.log("✅ Service: Returning data:", result.data);
        return { data: result.data as (SubscriptionUser & { id: string })[] };
      },
      providesTags: ["SubscriptionUser"],
    }),

    countSubscriptionUsers: builder.query<number, { where?: SubscriptionUsersFilter }>({
      queryFn: async ({ where = {} }) => {
        console.log("🔢 Service: Counting subscription users with filters:", where);
        const result = await executeQuery(() => subscriptionUserRepository.count(where));
        console.log("📊 Service: Count result:", result);
        return result as any;
      },
      providesTags: ["SubscriptionUser"],
    }),

    getSubscriptionUserById: builder.query<SubscriptionUser & { id: string }, string>({
      queryFn: async (id) => {
        const result = await executeQuery(() => subscriptionUserRepository.findById(id));
        if ("error" in result) return { error: result.error } as any;
        return { data: result.data as SubscriptionUser & { id: string } };
      },
      providesTags: ["SubscriptionUser"],
    }),

    getUserSubscriptions: builder.query<(import("src/types/subscription.types").UserSubscription & { id: string })[], string>({
      queryFn: async (userId) => {
        const result = await executeQuery(() => subscriptionUserRepository.getUserSubscriptions(userId));
        if ("error" in result) return { error: result.error } as any;
        return { data: result.data as (import("src/types/subscription.types").UserSubscription & { id: string })[] };
      },
      providesTags: ["SubscriptionUser"],
    }),

    getAllUsersWithSubscriptions: builder.query<
      (SubscriptionUser & { id: string })[],
      { where?: SubscriptionUsersFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        console.log("🚀 Service: Getting all users with previous subscriptions with filters:", where);
        const result = await executeQuery(() => subscriptionUserRepository.findAllUsersWithSubscriptions({ where, offset, limit }));
        console.log("📦 Service: All users query result:", result);
        
        if ("error" in result) {
          console.error("❌ Service: All users query error:", result.error);
          return { error: result.error } as any;
        }
        
        console.log("✅ Service: Returning all users data:", result.data);
        return { data: result.data as (SubscriptionUser & { id: string })[] };
      },
      providesTags: ["SubscriptionUser"],
    }),

    countAllUsersWithSubscriptions: builder.query<number, { where?: SubscriptionUsersFilter }>({
      queryFn: async ({ where = {} }) => {
        console.log("🔢 Service: Counting all users with previous subscriptions with filters:", where);
        const result = await executeQuery(() => subscriptionUserRepository.countAllUsersWithSubscriptions(where));
        console.log("📊 Service: All users count result:", result);
        return result as any;
      },
      providesTags: ["SubscriptionUser"],
    }),

    stopUserSubscription: builder.mutation<{ success: boolean }, { userId: string; subscriptionId: string }>({
      queryFn: async ({ userId, subscriptionId }) => {
        try {
          const result = await callFunctionSafe<{ success: boolean }>("stopUserSubscription", {
            userId,
            subscriptionId,
          });
          
          if ("error" in result) {
            return { error: { status: 500, data: result.error } };
          }
          
          if (!result.data?.success) {
            return { error: { status: 400, data: "Failed to stop subscription" } };
          }
          
          return { data: { success: true } };
        } catch (error) {
          console.error("Error calling stopUserSubscription:", error);
          return { error: { status: 500, data: error } };
        }
      },
      invalidatesTags: ["SubscriptionUser"],
    }),

    verifyStoreSubscription: builder.mutation<
      StoreSubscriptionVerification,
      { userId: string; subscriptionId: string }
    >({
      queryFn: async ({ userId, subscriptionId }) => {
        try {
          const result = await callFunctionSafe<StoreSubscriptionVerification>("verifyStoreSubscription", {
            userId,
            subscriptionId,
          });

          if ("error" in result) {
            return { error: { status: 500, data: result.error } };
          }

          return { data: result.data };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
    }),
    auditStoreSubscriptionPayments: builder.mutation<
      StoreSubscriptionPaymentAudit,
      { userId: string; subscriptionId: string }
    >({
      queryFn: async ({ userId, subscriptionId }) => {
        try {
          const result = await callFunctionSafe<StoreSubscriptionPaymentAudit>(
            "auditStoreSubscriptionPayments",
            {
              userId,
              subscriptionId,
            }
          );

          if ("error" in result) {
            return { error: { status: 500, data: result.error } };
          }

          return { data: result.data };
        } catch (error) {
          return { error: { status: 500, data: error } };
        }
      },
    }),
  }),
});

export const {
  useGetSubscriptionUsersQuery,
  useCountSubscriptionUsersQuery,
  useLazyCountSubscriptionUsersQuery,
  useGetSubscriptionUserByIdQuery,
  useGetUserSubscriptionsQuery,
  useGetAllUsersWithSubscriptionsQuery,
  useCountAllUsersWithSubscriptionsQuery,
  useLazyCountAllUsersWithSubscriptionsQuery,
  useStopUserSubscriptionMutation,
  useVerifyStoreSubscriptionMutation,
  useAuditStoreSubscriptionPaymentsMutation,
} = subscriptionUsersService;
